import React from 'react';

import { formatPercentage, numberShorthand } from '@benzinga/utils';
import styles from './index.module.scss';
import { SectionTitle } from '@benzinga/core-ui';
import { Earnings } from '@benzinga/calendar-manager';
import { useTranslation } from 'react-i18next';
import type { EChartsOption } from 'echarts';
import dayjs from 'dayjs';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

interface EarningsChartProps {
  data: Earnings[];
  type: 'eps' | 'revenue';
}

export const EarningsChart: React.FC<EarningsChartProps> = ({ data, type }) => {
  const { t } = useTranslation('quote');

  const isEps = type === 'eps';
  const quarterlyTitle = isEps
    ? t('Quote.Earnings.Charts.quarterly-eps-surprise')
    : t('Quote.Earnings.Charts.quarterly-revenue-surprise');
  const tableHeaders = [
    t('Quote.Earnings.Charts.table.fiscal-quarter'),
    t('Quote.Earnings.Charts.table.date-reported'),
    isEps ? t('Quote.Earnings.Charts.table.actual-eps') : t('Quote.Earnings.Charts.table.actual-revenue'),
    isEps ? t('Quote.Earnings.Charts.table.estimate-eps') : t('Quote.Earnings.Charts.table.estimate-revenue'),
    t('Quote.Earnings.Charts.table.surprise-percent'),
  ];

  const chartOptions: EChartsOption = React.useMemo(() => {
    const latest = data?.slice(0, 9)?.reverse() ?? [];
    const legend = isEps
      ? [
          t('Quote.Earnings.Charts.legend.estimated-eps'),
          t('Quote.Earnings.Charts.legend.reported-eps'),
          t('Quote.Earnings.Charts.legend.eps-surprise'),
        ]
      : [
          t('Quote.Earnings.Charts.legend.estimated-revenue'),
          t('Quote.Earnings.Charts.legend.reported-revenue'),
          t('Quote.Earnings.Charts.legend.revenue-surprise'),
        ];

    const beatText = t('Quote.EPS.Type.beat').toUpperCase();

    return {
      legend: {
        data: [legend[0], legend[1]],
        left: '50px',
        orient: 'horizontal',
        top: 'bottom',
      },
      series: [
        {
          data: latest.map(data => {
            return data[type + '_est'];
          }),
          itemStyle: {
            color: '#1d456b',
          },
          name: legend[0],
          type: 'bar',
        },
        {
          data: latest.map(data => {
            return data[type];
          }),
          itemStyle: {
            color: '#2e79f6',
          },
          name: legend[1],
          type: 'bar',
        },
        {
          data: latest.map(data => {
            const percent = data[type + '_surprise_percent'];
            return percent ? (parseFloat(percent) * 100).toFixed(2) + '%' : null;
          }),
          itemStyle: {
            color: 'orange',
            opacity: 0,
          },
          name: legend[2],
          type: 'scatter',
        },
      ],
      tooltip: {
        axisPointer: {
          crossStyle: {
            color: '#999',
          },
          type: 'cross',
        },
        formatter: function (params) {
          const title = params?.[0]?.axisValue?.replace(beatText, '');
          const rows = params?.map(row => {
            return `${row.marker}${row.seriesName}<span style="float: right; margin-left: 20px"><b>${row.value ? numberShorthand(row.value) : '-'}</b></span>`;
          });
          const tooltip = [title, ...rows].join('<br />');
          return tooltip;
        },
        trigger: 'axis',
      },
      xAxis: [
        {
          axisLabel: {
            formatter: function (value) {
              return value.includes(beatText) ? value.replace(beatText, `{style|${beatText}}`) : value;
            },
            rich: {
              style: {
                color: 'green',
                fontWeight: 'bold',
              },
            },
          },
          axisPointer: {
            type: 'shadow',
          },
          data: latest?.map(earning => {
            const period = earning.period + ' ' + earning.period_year;
            const surprisePct = earning[type + '_surprise_percent'];
            const surpriseAbs = earning[type + '_surprise'];
            // Prefer _surprise_percent (a small decimal, e.g. 0.0312 = 3.12%); fall back to
            // _surprise (absolute dollar/unit value) so a missing percent field doesn't
            // silently drop the beat label. A 0 surprise correctly shows no label either way.
            const hasBeat =
              surprisePct != null
                ? parseFloat(surprisePct) > 0
                : surpriseAbs != null
                  ? parseFloat(surpriseAbs) > 0
                  : false;
            return period + (hasBeat ? '\n ' + beatText : '');
          }),
          type: 'category',
        },
      ],
      yAxis: [
        {
          axisLabel: {
            formatter: function (value) {
              return `$${numberShorthand(value, isEps ? 2 : 0)}`;
            },
          },
          type: 'value',
        },
        {
          axisLabel: {
            interval: '0',
            textStyle: {
              color: '#fff',
            },
          },
          max: 100,
          min: 0,
          name: 'right_yaxis',
          nameTextStyle: {
            color: '#fff',
          },
          splitLine: {
            show: false,
          },
          splitNumber: 5,
          type: 'value',
        },
      ],
    };
  }, [isEps, t, data, type]);

  const filteredData = data.filter(row => row[type + '_surprise_percent']).slice(0, 4);
  const hasData = filteredData.length > 0;

  return (
    <div className={styles.earningsChartWrapper}>
      <ReactECharts
        className="z-[1]"
        key="performance"
        notMerge={true}
        option={chartOptions}
        style={{ height: '425px', overflow: 'hidden', width: '100%' }}
      />
      {hasData && (
        <div className="mt-8">
          <SectionTitle level={4} size="lg" uppercase={false}>
            {quarterlyTitle}
          </SectionTitle>
          <table className="simple-earnings-table mt-4">
            <thead>
              <tr className="table-row">
                {tableHeaders.map((header, idx) => {
                  // Force each header word onto its own line. The header row is a
                  // 5×1fr grid; a single-line header sets its track's min width and
                  // overflows the column. Breaking on spaces keeps each track as
                  // narrow as its widest word so the table fits its container.
                  const words = header.split(' ');
                  return (
                    <th className="table-title" key={idx}>
                      {words.map((word, wIdx) => (
                        <React.Fragment key={wIdx}>
                          {wIdx > 0 && <br />}
                          {word}
                        </React.Fragment>
                      ))}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="mt-2">
              {filteredData.map((row, idx) => {
                return (
                  <tr className="table-row body-row" key={idx}>
                    <td className="font-bold">{row.period}</td>
                    <td>{row.date ? dayjs(row.date).format('MMM D, YYYY') : '—'}</td>
                    <td>${numberShorthand(row[type], 2)}</td>
                    <td>${numberShorthand(row[type + '_est'], 2)}</td>
                    <td>{formatPercentage(row[type + '_surprise_percent'] * 100)} %</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EarningsChart;
